import { State, Action, StateContext, Selector, Store } from "@ngxs/store";
import { inject, Injectable } from "@angular/core";
import { LeadsService } from "../leads.service";
import { catchError, from, tap } from "rxjs";
import { OnCanCloseDialog, OnSetSharedDropdowns, SetShowLoader, ShowToastrAlert } from "app/modules/shared-state/shared.actions";
import { OnRemoveSharedGridRowData, OnSetSharedGridConfig, OnSetSharedGridRowData } from "app/modules/shared/ag-grid/shared-grid-state/shared-grid.actions";
import { SharedGridState } from "app/modules/shared/ag-grid/shared-grid-state/shared-grid.state";
import { DropdownOptionsService } from "app/modules/system-setup/dropdown-options/dropdown-options.service";
import { DropdownOption, Lead, LeadsStateModel } from "./leads.models";
import { CreateLead, DeleteLead, GetLeads, GetLeadsForFormData, GetLeadsStatuses, GetTobaccoForms, GetTobaccoTypes, OnActionLead, OnSetLeadStatus, SetLeadFormGridConfig, SetSelectedLead, UpdatedLeadWebhook } from "./leads.actions";
import { SetLeadType } from "./leads.actions";
import { produce } from "immer";
import { MatDialog } from "@angular/material/dialog";
import { DropdownComponent } from "app/modules/shared/dropdown/dropdown.component";
import { ResetForm, UpdateFormValue } from "@ngxs/form-plugin";
import { SharedState } from "app/modules/shared-state/shared.state";
import { Router } from "@angular/router";

@State<LeadsStateModel>({
    name: 'leads',
    defaults: {
        leads: [],
        selectedLead: { _id: null, md5Checksum: null },
        leadType: 'Customers',
        canOpenDialog: false,
        tobaccoTypes: [],
        leadForm: {
            model: {
                stakeholder: null,
                grade: null,
                cropYear: null,
                tobaccoType: null,
                tobaccoForm: null,
                nicotine: null,
                sugar: null,
                netKgs: null,
                price: null,
                customerType: null,
                status: null,
                supplyDate: null,
                stakeholderType: null
            },
            dirty: false,
            status: '',
            errors: {}
        },
        tobaccoForms: [],
        formDropdowns: {},
    }
})
@Injectable()
export class LeadsState {
    private _leadsService = inject(LeadsService);
    private _dropdownService = inject(DropdownOptionsService);
    private _store = inject(Store);
    private _router = inject(Router);

    @Action(SetLeadType)
    setLeadType(ctx: StateContext<LeadsStateModel>, { payload }: SetLeadType) {
        ctx.patchState({ leadType: payload.leadType });
    }

    @Selector()
    static getLeadType(state: LeadsStateModel) {
        return state.leadType;
    }

    @Action(GetLeads)
    getLeads(ctx: StateContext<LeadsStateModel>, { payload }: GetLeads) {
        ctx.dispatch(new SetShowLoader(true));
        const leadType = ctx.getState().leadType;
        return this._leadsService.getLeads(leadType).pipe(
            catchError((error) => {
                console.error('Error getting leads:', error);
                ctx.dispatch(new ShowToastrAlert({
                    showToast: true,
                    toastrType: 'error',
                    messageTitle: 'Error',
                    messageBody: `Error getting leads: ${error.error.message.message}`,
                    position: 'toast-top-center'
                }));
                ctx.dispatch(new SetShowLoader(false));
                throw error;
            }),
            tap((leads) => {
                ctx.dispatch(new OnSetSharedGridRowData({ reload: true, payload: leads, gridUUID: payload.gridUUID }));
                ctx.dispatch(new SetShowLoader(false));
            })
        );
    }

    @Action(SetSelectedLead)
    setSelectedLead(ctx: StateContext<LeadsStateModel>, { payload }: SetSelectedLead) {
        ctx.patchState({ selectedLead: payload });
    }

    @Selector()
    static getSelectedLead(state: LeadsStateModel) {
        return state.selectedLead;
    }

    @Action(CreateLead)
    createLead(ctx: StateContext<LeadsStateModel>) {
        const leadForm = ctx.getState().leadForm;
        leadForm.model['leadType'] = ctx.getState().leadType;
        leadForm.model['status'] = 'PENDING';
        leadForm.model.grade = leadForm.model.grade.toUpperCase();
        return this._leadsService.createLead(leadForm.model).pipe(
            catchError((error) => {
                console.error('Error creating lead:', error);
                ctx.dispatch(new ShowToastrAlert({
                    showToast: true,
                    toastrType: 'error',
                    messageTitle: 'Error',
                    messageBody: `Error creating lead: ${error.error.message.message}`,
                    position: 'toast-top-center'
                }));
                ctx.dispatch(new SetShowLoader(false));
                throw error;
            }),
            tap((newLead: Lead) => {
                ctx.dispatch(new OnSetSharedGridRowData({ payload: [newLead], gridUUID: this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).firstGridUUID }));
                ctx.dispatch(new SetShowLoader(false));
                ctx.dispatch(new OnCanCloseDialog(true));
                ctx.dispatch(new SetSelectedLead({ _id: null, md5Checksum: null }));
                ctx.dispatch(new ResetForm({ path: 'leads.leadForm' }));
                ctx.dispatch(new ShowToastrAlert({
                    showToast: true,
                    toastrType: 'success',
                    messageTitle: 'Success',
                    messageBody: `Lead created for ${newLead.stakeholder}`,
                    position: 'toast-top-center'
                }));
            })
        );
    }

    @Action(DeleteLead)
    deleteLead(ctx: StateContext<LeadsStateModel>, { payload }: DeleteLead) {
        ctx.dispatch(new SetShowLoader(true));
        return this._leadsService.deleteLead(payload._id).pipe(
            catchError((error) => {
                console.error('Error deleting lead:', error);
                ctx.dispatch(new ShowToastrAlert({
                    showToast: true,
                    toastrType: 'error',
                    messageTitle: 'Error',
                    messageBody: `Error deleting lead: ${error.error.message.message}`,
                    position: 'toast-top-center'
                }));
                ctx.dispatch(new SetShowLoader(false));
                throw error;
            }),
            tap(() => {
                ctx.dispatch(new OnRemoveSharedGridRowData({
                    gridUUID: this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).firstGridUUID,
                    removeRow: payload
                }));
                ctx.dispatch(new SetShowLoader(false));
                ctx.dispatch(new ShowToastrAlert({
                    showToast: true,
                    toastrType: 'success',
                    messageTitle: 'Success',
                    messageBody: `Lead deleted for ${payload.stakeholder}`,
                    position: 'toast-top-center'
                }));
            })
        );
    }

    @Action(OnActionLead)
    onActionLead(ctx: StateContext<LeadsStateModel>) {
        ctx.dispatch(new SetShowLoader(true));
        const selectedLead = ctx.getState().selectedLead;
        const leadForm = ctx.getState().leadForm;
        if (selectedLead._id) {
            return from(this.onUpdateLead(ctx, {
                ...leadForm.model,
                _id: selectedLead._id,
                md5Checksum: selectedLead.md5Checksum
            }, selectedLead.md5Checksum));
        } else {
            return ctx.dispatch(new CreateLead());
        }

    }


    async onUpdateLead(ctx: StateContext<LeadsStateModel>, payload: Lead, md5Checksum: string) {
        payload.grade = payload.grade.toUpperCase();
        return this._leadsService.updateLead(payload, md5Checksum).pipe(
            catchError((error) => {
                console.error('Error updating lead:', error);
                ctx.dispatch(new SetShowLoader(false));
                ctx.dispatch(new ShowToastrAlert({
                    showToast: true,
                    toastrType: 'error',
                    messageTitle: 'Error',
                    messageBody: `Error updating lead: ${error.error.message.message}`,
                    position: 'toast-top-center'
                }));
                ctx.dispatch(new SetShowLoader(false));
                throw error;
            }),


            tap(() => {
                ctx.dispatch(new SetShowLoader(false));
                ctx.dispatch(new OnCanCloseDialog(true));
                ctx.dispatch(new SetSelectedLead({ _id: null, md5Checksum: null }));
                ctx.dispatch(new ResetForm({ path: 'leads.leadForm' }));
                ctx.dispatch(new ShowToastrAlert({
                    showToast: true,
                    toastrType: 'success',
                    messageTitle: 'Success',
                    messageBody: `Lead updated for ${payload.stakeholder}`,
                    position: 'toast-top-center'
                }));
            })
        );
    }

    @Action(SetLeadFormGridConfig)
    setLeadFormGridConfig(ctx: StateContext<LeadsStateModel>, { payload }: SetLeadFormGridConfig) {
        const store = inject(Store);
        const parentGridConfig = store.selectSnapshot(SharedGridState.getGridConfig);

        const selectedLead: Lead = parentGridConfig[payload.gridUUID].rowData.find(lead => lead._id === ctx.getState().selectedLead._id);
        if (selectedLead) {
            return this._leadsService.getLeads(ctx.getState().leadType).pipe(
                tap((leads: Lead[]) => {
                    const updatedGridConfig = produce(parentGridConfig, (draft) => {
                        draft[store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).secondGridUUID] = payload.secondGridConfig[store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).secondGridUUID];
                        draft[store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).secondGridUUID].rowData = leads;
                    });
                    ctx.dispatch(new OnSetSharedGridConfig(updatedGridConfig));
                })
            );
        } else {
            const updatedGridConfig = produce(parentGridConfig, (draft) => {
                draft[store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).secondGridUUID] = payload.secondGridConfig[store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).secondGridUUID];
            });
            ctx.dispatch(new OnSetSharedGridConfig(updatedGridConfig));
        }
    }

    @Action(GetLeadsForFormData)
    getLeadsForFormData(ctx: StateContext<LeadsStateModel>, { payload }: GetLeadsForFormData) {
        return this._leadsService.getLeads(payload).pipe(
            tap((leads: Lead[]) => {
                ctx.dispatch({ leads: leads });
            })
        );
    }

    @Selector()
    static getLeadsForFormData(state: LeadsStateModel) {
        return state.leads;
    }

    @Action(GetTobaccoTypes)
    getTobaccoTypes(ctx: StateContext<LeadsStateModel>) {

        return this._leadsService.getTobaccoTypes().pipe(
            tap((tobaccoTypes: any[]) => {
                const formattedTobaccoTypes: DropdownOption[] = tobaccoTypes.map(item => ({
                    id: item.id?.toString() ?? '',
                    value: item.value ?? ''
                }));

                ctx.patchState({ tobaccoTypes: formattedTobaccoTypes });
            }),

            catchError((error) => {
                console.error('Error fetching tobacco types:', error);
                this._store.dispatch(new ShowToastrAlert({
                    showToast: true,
                    toastrType: 'error',
                    messageTitle: 'Error',
                    messageBody: `Failed to load tobacco types: ${error.message}`,
                    position: 'toast-top-center'
                }));

                return [];
            })
        );
    }

    @Selector()
    static getTobaccoTypes(state: LeadsStateModel) {
        return state.tobaccoTypes;
    }

    //** Leads Status Functions */

    @Action(GetLeadsStatuses)
    getLeadsStatuses(ctx: StateContext<LeadsStateModel>) {
        const dialogRef = inject(MatDialog);
        return this._leadsService.getDropdownsFilter({ types: ['leadStatuses'] }).pipe(
            catchError((error) => {
                ctx.dispatch(new SetShowLoader(false));
                ctx.dispatch(new ShowToastrAlert({
                    showToast: true,
                    toastrType: 'error',
                    messageTitle: 'Error',
                    messageBody: error.error.message.message,
                    position: 'toast-top-center'
                }));
                throw error;
            }),
            tap((response: { leadStatuses: string[]; }) => {
                ctx.dispatch(new OnSetSharedDropdowns(response.leadStatuses));
                const _dialog = dialogRef.open(DropdownComponent, {
                    width: '320px',
                    data: {
                        header: 'Change Status',
                        body: 'Please select the new status for the lead',
                    }
                });
                _dialog.afterClosed().subscribe((result) => {
                    if (!result) return;
                    this._store.dispatch(new UpdateFormValue({
                        path: 'shared.statusForm',
                        value: {
                            status: result
                        }
                    }));
                    ctx.dispatch(new OnSetLeadStatus());

                });
            })
        );
    }

    @Action(OnSetLeadStatus)
    onSetLeadStatus(ctx: StateContext<LeadsStateModel>) {
        const dropdownStatus = this._store.selectSnapshot(SharedState.getStatusForm).model.status;
        ctx.dispatch(new SetShowLoader(true));
        const selectedLead = ctx.getState().selectedLead;
        return this._leadsService.updateLeadStatus(
            selectedLead._id,
            dropdownStatus
        ).pipe(
            //TODO: Add an interface for the response that is generic for this return as its used all over
            catchError((error) => {
                ctx.dispatch(new SetShowLoader(false));
                ctx.dispatch(new ShowToastrAlert({
                    showToast: true,
                    toastrType: 'error',
                    messageTitle: 'Error',
                    messageBody: error.error.message.message,
                    position: 'toast-top-center'
                }));
                throw error;
            }),
            tap((response: Lead) => {
                ctx.dispatch(new SetShowLoader(false));
                ctx.dispatch(new OnSetSharedGridRowData({
                    update: true,
                    payload: [response],
                    gridUUID: this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).firstGridUUID
                }));
                ctx.dispatch(new SetSelectedLead({ _id: null, md5Checksum: null }));
                ctx.dispatch(new ShowToastrAlert({
                    showToast: true,
                    toastrType: 'success',
                    messageTitle: 'Success',
                    messageBody: 'Lead status updated successfully',
                    position: 'toast-top-center'
                }));

            })
        );
    }

    @Action(UpdatedLeadWebhook)
    updatedLeadWebhook(ctx: StateContext<LeadsStateModel>, { payload }: UpdatedLeadWebhook) {
        const isOnLeadsScreen = this._router.url.includes('leads');
        if (payload && isOnLeadsScreen) {
            ctx.dispatch(new GetLeads({
                gridUUID: this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).firstGridUUID
            }));
        }
    }

    @Action(GetTobaccoForms)
    getTobaccoForms(ctx: StateContext<LeadsStateModel>) {

        return this._leadsService.getTobaccoForms().pipe(
            tap((tobaccoForms: any[]) => {
                const formattedTobaccoForms: DropdownOption[] = tobaccoForms.map(item => ({
                    id: item.id?.toString() ?? '',
                    value: item.value ?? ''
                }));

                ctx.patchState({ tobaccoForms: formattedTobaccoForms });
            }),

            catchError((error) => {
                // ctx.patchState({ error: error.message });

                this._store.dispatch(new ShowToastrAlert({
                    showToast: true,
                    toastrType: 'error',
                    messageTitle: 'Error',
                    messageBody: `Failed to load tobacco forms: ${error.message}`,
                    position: 'toast-top-center'
                }));

                return [];
            })
        );
    }

    @Selector()
    static getTobaccoForms(state: LeadsStateModel) {
        return state.tobaccoForms;
    }

}